import { NgModule, APP_INITIALIZER, inject } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { ReactiveFormsModule, FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HashLocationStrategy, LocationStrategy, CommonModule } from '@angular/common';
import { HttpClientModule, HttpClientJsonpModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { DecimalPipe } from '@angular/common';
import { TagInputModule } from 'ngx-chips';


// auth
import { MsalGuard, MsalInterceptor, MsalModule, MsalGuardConfiguration, MsalInterceptorConfiguration, MsalBroadcastService, MSAL_INSTANCE, MSAL_GUARD_CONFIG, MSAL_INTERCEPTOR_CONFIG, MsalService } from '@azure/msal-angular';

import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { UnauthorisedComponent } from './components/unauthorised/unauthorised.component';
import { LoginComponent } from './components/login/login.component';
import { LogoutComponent } from './components/logout/logout.component';
//import { TestPageComponent } from './test-page/test-page.component';

// layouts
import { FireForesightLayoutComponent } from './components/layouts/fireForesightLayout.component';
import { BlankLayoutComponent } from './components/layouts/blankLayout.component';
import { BasicLayoutComponent } from './components/layouts/basicLayout.component';
import { MapLayoutComponent } from './components/layouts/mapLayout.component';
import { PublicLayoutComponent } from './components/layouts/publicLayout.component';
import { EmbedLayoutComponent } from './components/layouts/embedLayout.component';
import { CameraWatchLayoutComponent } from './components/layouts/cameraWatchLayout.component';
import { NavigationComponent } from './components/common/navigation/navigation.component';
import { HeaderComponent } from './components/common/header/header.component';

// Services
import { AzureAuthService } from 'src/service/azure-auth.service';
import { PublicBypassInterceptor } from 'src/service/public-bypass.interceptor';
import { ApplicationConfigService } from 'src/service/application-config.service';
import { DataBusTableService } from 'src/service/databus.service';
import { UserTableService } from 'src/service/user.service';
import { CustomerTableService } from 'src/service/customer.service';
import { SiteTypeTableService } from 'src/service/site-type.service';
import { ProductTableService } from 'src/service/product.service';
import { DashboardTableService } from 'src/service/dashboard.service';
import { DashboardListTableService } from 'src/service/dashboard-list.service';
import { DistributionTableService } from 'src/service/distribution.service';
import { MessageTemplateTableService } from 'src/service/message-template.service';
import { IconService } from 'src/service/icon.service';
import { ElapsedTimeService } from 'src/service/elapsed-time.service';
import { StorageTablesService } from 'src/service/storage-tables.service';
import { WidgetService } from 'src/service/widget.service';
import { AbstractDataService } from 'src/service/data.service';
import { ScriptService } from 'src/service/script.service';
import { DownloadCsvService } from 'src/service/download-csv.service';
import { DashboardService,DataService,CustomerService, EucsAlarmService } from 'src/api/services';

// pipes
import { RelativeDatePipe } from './pipes/relative-date.pipe';
import { FindUsersByIdsPipe } from './pipes/find-users-by-ids.pipe';
import { FindPluginsByCategoryPipe } from './pipes/find-plugins-by-category.pipe';
import { DashboardFilterPipe } from './pipes/dashboard/dashboard-filter.pipe';
import { GetWidgetSizePipe } from './pipes/dashboard/get-widget-size.pipe';
import { GetWidgetTypePipe } from './pipes/dashboard/get-widget-type.pipe';
import { TimeSeriesSortPipe } from './pipes/time-series-sort-pipe';
import { SafePipe } from './pipes/safe.pipe';
import { LightStackFilter } from './pipes/dashboard/lightstack-filter.pipe';
import { IncidentPipe } from './pipes/incident-pipe';
import { FlowTotalPipe } from './pipes/eucs/flow-total.pipe';
import { EndpointStatusPipe } from './pipes/eucs/endpoint-status.pipe';
import { FindCustomerUsagePipe } from './pipes/eucs/find-customer-usage.pipe';
import { CustomerFilterPipe } from './pipes/eucs/customer-filter.pipe';

// Components

// - directives
import { VarDirective } from './directives/var.directive';
import { IFrameResizerDirective } from './directives/iframe-resiser.directive';

// - shared
import { TableComponent } from 'src/app/components/table/table.component';
import { AlertsComponent } from 'src/app/components/alerts/alerts.component';
import { AlarmEditorComponent } from 'src/app/components/alarm-editor/alarm-editor.component';
import { FileListComponent } from 'src/app/components/file-list/file-list.component'
import { UserListComponent } from './components/user-list/user-list.component';
import { TimePeriodComponent } from './components/time-period/time-period.component';
import { TransformEditorComponent } from './components/transform-editor/transform-editor.component';
//import { WidgetSelectComponent } from './components/widget-select/widget-select.component';
import { ParameterComponent } from './components/parameter/parameter.component';
import { GradientSelectorComponent } from './components/gradient-selector/gradient-selector.component';
import { OpenStreetMapComponent } from './components/open-street-map/open-street-map.component';
import { MapComponent } from './components/map/map.component';
import { ImageOverlayComponent } from './components/image-overlay/image-overlay.component';
import { GaugeComponent } from './components/gauge/gauge.component';
import { WeatherStationViewer } from './components/weather-station-viewer/weather-station-viewer.component';
import { CameraControllerComponent } from './components/camera-controller/camera-controller.component';
import { ImageClassifierComponent } from './components/image-classifier/image-classifier.component';
import { KeyValueEditorComponent } from './components/key-value-editor.ts/key-value-editor.component';
import { JsonPopupComponent } from './components/json-popup/json-popup.component';

// Views

import { HomeComponent } from './views/home/home.component';
import { OnBoardingComponent } from './views/on-boarding/on-boarding.component';
import { MarketplaceComponent } from './views/applications/marketplace.component';
import { CeresTagHomeComponent } from './views/applications/ceres-tag/home/home.component';
import { CeresTagFarmComponent } from './views/applications/ceres-tag/farm/farm.component';
import { CeresTagOnBoardComponent } from './views/applications/ceres-tag/onboard/onboard.component';
import { RoomAirQualityHomeComponent } from './views/applications/room-air-quality/home.component';
import { FireForesightIncidentComponent } from './views/applications/fire-foresight/incident/fire-foresight-incident.component';
import { FireForesightIncidentEmbeddedComponent } from './views/applications/fire-foresight/incident-embedded/fire-foresight-incident-embedded.component';
import { FireForesightIncidentsComponent } from './views/applications/fire-foresight/incidents/fire-foresight-incidents.component';
import { FireForesightStatisticsComponent } from './views/applications/fire-foresight/statistics/fire-foresight-statistics.component';
import { FireForesightCameraComponent } from './views/applications/fire-foresight/camera/fire-foresight-camera.component';
import { FireForesightCamerasComponent } from './views/applications/fire-foresight/cameras/fire-foresight-cameras.component';
import { FireForesightOnBoardComponent } from './views/applications/fire-foresight/onboard/onboard.component';
import { FireForesightBurnListComponent } from './views/applications/fire-foresight/burn-list/fire-foresight-burn-list.component';
import { FireForesightBurnListOverviewComponent } from './views/applications/fire-foresight/burn-list-overview/fire-foresight-burn-list-overview.component';
import { FireForesightBurnCoupsComponent } from './views/applications/fire-foresight/burn-coups/fire-foresight-burn-coups.component';
import { FireForesightBurnListMapComponent } from './views/applications/fire-foresight/burn-list-map/fire-foresight-burn-list-map.component';
import { FireForesightWatchComponent } from './views/applications/fire-foresight/watch/fire-foresight-watch.component';
import { WatchCameraViewComponent } from './components/watch-camera-view/watch-camera-view.component';
import { CameraSelectionModalComponent } from './components/camera-selection-modal/camera-selection-modal.component';
import { CameraWatchComponent } from './views/camera-watch/camera-watch.component';
import { GenericCameraSelectionModalComponent } from './components/generic-camera-selection-modal/generic-camera-selection-modal.component';

// - data analysis*/
import { DataAnalysisComponent } from './views/data-analysis/data-analysis.component';
//import { TestImageAnalysisComponent } from './views/test-image-analysis/test-image-analysis.component';

// - Dashboard
import { DashboardComponent } from './views/dashboard/dashboard.component';
// -- Directives
import { WidgetDirective } from './views/dashboard/directives/widget.directive';
import { WidgetEditorDirective } from './views/dashboard/directives/widget-editor.directive';
// -- Widgets
import { WidgetComponent } from './views/dashboard/widgets/widget/widget.component';
import { ChartWidgetComponent } from './views/dashboard/widgets/chart-widget/chart-widget.component';
import { FreeTextWidgetComponent } from './views/dashboard/widgets/free-text-widget/free-text-widget.component';
import { GaugeWidgetComponent } from 'src/app/views/dashboard/widgets/gauge-widget/gauge-widget.component';
import { TableWidgetComponent } from 'src/app/views/dashboard/widgets/table-widget/table-widget.component';
import { ControlPanelWidgetComponent } from 'src/app/views/dashboard/widgets/control-panel-widget/control-panel-widget.component';
import { LatestImageWidgetComponent } from 'src/app/views/dashboard/widgets/latest-image-widget/latest-image-widget.component';
import { LatestSimpleWidgetComponent } from 'src/app/views/dashboard/widgets/latest-simple-widget/latest-simple-widget.component';
import { NewLineWidgetComponent } from './views/dashboard/widgets/new-line-widget/new-line-widget.component';
import { OpenStreetMapWidgetComponent } from './views/dashboard/widgets/open-street-map-widget/open-street-map-widget.component';
import { WindRoseWidgetComponent } from 'src/app/views/dashboard/widgets/wind-rose-widget/wind-rose-widget.component';
import { MediaPlayerWidgetComponent } from 'src/app/views/dashboard/widgets/media-player-widget/media-player-widget.component';
import { TimeLapseWidgetComponent } from 'src/app/views/dashboard/widgets/time-lapse-widget/time-lapse-widget.component';
import { IFrameWidgetComponent } from 'src/app/views/dashboard/widgets/iframe-widget/iframe-widget.component';
import { LatestAdvancedWidgetComponent } from 'src/app/views/dashboard/widgets/latest-advanced-widget/latest-advanced-widget.component';
import { VarianceWidgetComponent } from 'src/app/views/dashboard/widgets/variance-widget/variance-widget.component';
import { ImageOverlayWidgetComponent } from 'src/app/views/dashboard/widgets/image-overlay-widget/image-overlay-widget.component';
import { SizeComparisonWidgetComponent } from 'src/app/views/dashboard/widgets/size-comparison-widget/size-comparison-widget.component';
import { MultiTextWidgetComponent } from './views/dashboard/widgets/multi-text-widget/multi-text-widget.component';
import { PieChartWidgetComponent } from './views/dashboard/widgets/pie-chart-widget/pie-chart-widget.component'
import { OverdueSitesWidgetComponent } from './views/dashboard/widgets/overdue-sites-widget/overdue-sites-widget.component';

// -- Editors
import { WidgetEditorComponent } from './views/dashboard/editors/widget-editor/widget-editor.component';
import { ChartWidgetEditorComponent } from './views/dashboard/editors/chart-widget-editor/chart-widget-editor.component';
import { FreeTextWidgetEditorComponent } from './views/dashboard/editors/free-text-widget-editor/free-text-widget-editor.component';
import { GaugeWidgetEditorComponent } from 'src/app/views/dashboard/editors/gauge-widget-editor/gauge-widget-editor.component';
import { TableWidgetEditorComponent } from 'src/app/views/dashboard/editors/table-widget-editor/table-widget-editor.component';
import { ControlPanelWidgetEditorComponent } from 'src/app/views/dashboard/editors/control-panel-widget-editor/control-panel-widget-editor.component';
import { LatestImageWidgetEditorComponent } from 'src/app/views/dashboard/editors/latest-image-widget-editor/latest-image-widget-editor.component';
import { LatestSimpleWidgetEditorComponent } from 'src/app/views/dashboard/editors/latest-simple-widget-editor/latest-simple-widget-editor.component';
import { NewLineWidgetEditorComponent } from './views/dashboard/editors/new-line-widget-editor/new-line-widget-editor.component';
import { OpenStreetMapWidgetEditorComponent } from './views/dashboard/editors/open-street-map-widget-editor/open-street-map-widget-editor.component';
import { TelemetryMapWidgetEditorComponent } from './views/dashboard/editors/telemetry-map-widget-editor/telemetry-map-widget-editor.component';
import { WindRoseWidgetEditorComponent } from 'src/app/views/dashboard/editors/wind-rose-widget-editor/wind-rose-widget-editor.component';
import { MediaPlayerWidgetEditorComponent } from 'src/app/views/dashboard/editors/media-player-widget-editor/media-player-widget-editor.component';
import { TimeLapseWidgetEditorComponent } from 'src/app/views/dashboard/editors/time-lapse-widget-editor/time-lapse-widget-editor.component';
import { IFrameWidgetEditorComponent } from 'src/app/views/dashboard/editors/iframe-widget-editor/iframe-widget-editor.component';
import { LatestAdvancedWidgetEditorComponent } from 'src/app/views/dashboard/editors/latest-advanced-widget-editor/latest-advanced-widget-editor.component';
import { VarianceWidgetEditorComponent } from 'src/app/views/dashboard/editors/variance-widget-editor/variance-widget-editor.component';
import { ImageOverlayWidgetEditorComponent } from 'src/app/views/dashboard/editors/image-overlay-widget-editor/image-overlay-widget-editor.component';
import { SizeComparisonWidgetEditorComponent } from 'src/app/views/dashboard/editors/size-comparison-widget-editor/size-comparison-widget-editor.component';
import { MultiTextWidgetEditorComponent } from './views/dashboard/editors/multi-text-widget-editor/multi-text-widget-editor.component';
import { PieChartWidgetEditorComponent } from './views/dashboard/editors/pie-chart-widget-editor/pie-chart-widget-editor.component'
import { OverdueSitesWidgetEditorComponent } from './views/dashboard/editors/overdue-sites-widget-editor/overdue-sites-widget-editor.component';


// - customer
import { CustomerListComponent } from './views/customer/customer-list/customer-list.component';
import { CustomerSpecificComponent } from './views/customer/customer-specific/customer-specific.component';
import { ProductInstanceSpecificComponent } from './views/customer/product-instance-specific/product-instance-specific.component';
import { OrganisationalSettingsComponent } from './views/organisational-settings/organisational-settings.component';

// user pages
import { ApiKeysComponent } from './views/api-keys/api-keys.component';

// - site type
import { SitesComponent } from './views/sites/sites.component';
import { SiteTypeComponent } from './views/site-type/site-type.component';
import { SiteTypesComponent } from './views/site-types/site-types.component';

// - users
import { UsersComponent } from './views/users/users.component';

// - control
import { ControlComponent } from './views/control/control.component';

// - products
import { ProductsComponent } from './views/products/products.component';

// - customers
import { CustomersComponent } from './views/customers/customers.component';

// - products*/
import { DashboardsComponent } from './views/dashboards/dashboards.component';
import { DashboardListComponent } from './views/dashboard-list/dashboard-list.component';

// - databus
import { DatabusOverviewComponent } from './views/databus/databus-overview/databus-overview.component';
import { DatabusPurchaseComponent } from './views/databus/databus-purchase/databus-purchase.component';
import { DatabusApproveComponent } from './views/databus/databus-approve/databus-approve.component';
import { DatabusHealthComponent } from './views/databus/databus-health/databus-health.component';

// - distribution
import { MessageTemplateComponent } from './views/message-template/message-template.component';
import { DistributionComponent } from './views/distribution/distribution.component';

import { DynamicTemplateComponent } from './components/dynamic-template/dynamic-template.component';
import { SmartImageComponent } from './components/smart-image/smart-image.component';
import { SmartImageListComponent } from './components/smart-image-list/smart-image-list.component';

// - eucs
import { EucsHomeComponent } from './views/applications/end-user-control-system/home/home.component';
import { WindroseHomeComponent } from './views/applications/end-user-control-system/windrose-home/windrose-home.component';

// Plugins
import { SortableComponent, SortableModule } from 'ngx-bootstrap/sortable';
import { BsDatepickerModule } from 'ngx-bootstrap/datepicker';
import { TooltipModule  } from 'ngx-bootstrap/tooltip';
import { PopoverModule } from 'ngx-bootstrap/popover';
import { ToastrModule } from 'ngx-toastr';
import { TabsModule } from 'ngx-bootstrap/tabs';
import { CollapseModule } from 'ngx-bootstrap/collapse';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { ModalModule } from 'ngx-bootstrap/modal';
import { HighchartsChartModule } from 'highcharts-angular';
import { TrendModule } from 'ngx-trend';
//import { IgxGridModule, IgxHierarchicalGridModule , IgxDialogModule, IgxComboModule, IgxActionStripModule } from 'modules/igniteui-angular';
import { SortablejsModule } from 'ngx-sortablejs';
import { ColorPickerModule } from 'ngx-color-picker';
import { MonacoEditorModule, NgxMonacoEditorConfig } from 'ngx-monaco-editor-v2';
import { BsDropdownConfig, BsDropdownModule } from 'ngx-bootstrap/dropdown';
import { PaginationModule } from 'ngx-bootstrap/pagination';
import { NgxFileDropModule } from 'ngx-file-drop';
import { NgbDropdownModule, NgbModalModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap';
import { CookieService } from 'ngx-cookie-service';
import { ClipboardModule } from 'ngx-clipboard';
import { AngularEditorModule } from '@kolkov/angular-editor';
import { FireService } from 'src/service/fire.service';
import { EucsAlarmDashboardService } from 'src/service/eucs-alarm-dashboard.service';
import { MapLayersComponent } from './views/map-layers/map-layers.component';
import { GeoAssetsComponent } from './views/geo-assets/geo-assets.component';
import { GeoAssetEditorComponent } from './views/geo-assets/editor/geo-asset-editor.component';
import { MapLayerTableService } from 'src/service/map-layer.service';
import { TestPageComponent } from './test-page/test-page.component';
import { FireImageClassifierComponent } from './components/fire-image-classifier/fire-image-classifier.component';
import { PublicHeaderComponent } from './components/common/public-header/public-header.component';
import { DashboardTabsComponent } from './components/dashboard-tabs/dashboard-tabs.component';
import { SiteTypeFilterPipe } from './pipes/site-type-filter-pipe';
import { FireForesightMenuComponent } from './views/applications/fire-foresight/menu/menu.component';
import { NgForIfEmpty } from './directives/NgForIfEmpty';
import { VideoPlayerComponent } from './components/video-player/video-player.component';
import { VirtualTimeSeriesEditorComponent } from './components/virtual-time-series-editor/virtual-time-series-editor.component';
import { SiteMapComponent } from './views/site-map/site-map.component';
import { ClockWidgetComponent } from './views/dashboard/widgets/clock-widget/clock-widget.component';
import { ClockWidgetEditorComponent } from './views/dashboard/editors/clock-widget-editor/clock-widget-editor.component';
import { DateTimeComponent } from './components/date-time/date-time.component';
import { ImageZoneEditorComponent } from './components/image-zone-editor/image-zone-editor.component';
import { PanoramaComponent } from './views/applications/fire-foresight/panorama/panorama.component';
import { SiteComponent } from './views/site/site.component';
import { BrowserCacheLocation, InteractionStatus, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { AzureAuthGuard } from './guards/azure-auth.guard';
import { VisibleWidgetPipe } from './views/dashboard/base/visible-widget-pipe';
import { CssLoaderService } from 'src/service/css-loader.service';
import { DashboardWidgetComponent } from './views/dashboard/widgets/dashboard-widget/dashboard-widget.component';
import { DashboardWidgetEditorComponent } from './views/dashboard/editors/dashboard-widget-editor/dashboard-widget-editor.component';
import { SupportHomeComponent } from './views/support/support-home/support-home.component';
import { SupportSearchComponent } from './views/support/support-search/support-search.component';
import { WidgetTextTemplatesComponent } from './views/widget-text-templates/widget-text-templates.component';
import { TagEditorComponent } from './components/tag-editor/tag-editor.component';
import { SmartIrrigationDashboardComponent } from './views/applications/smart-irrigation/dashboard/dashboard.component';
import { SmartIrrigationOnBoardComponent } from './views/applications/smart-irrigation/onboard/onboard.component';
import { SmartIrrigationSchedulesComponent } from './views/applications/smart-irrigation/schedules/schedules.component';
import { WeeklyScheduleEditorComponent } from './views/applications/smart-irrigation/components/weekly-schedule-editor/weekly-schedule-editor.component';
import { AutomatedScheduleEditorComponent } from './views/applications/smart-irrigation/components/automated-schedule-editor/automated-schedule-editor.component';
import { DashboardEventService } from './views/dashboard/dashboard-event.service';
import { SmartIrrigationRegionsComponent } from './views/applications/smart-irrigation/regions/regions.component';
import { SmartIrrigationRegionComponent } from './views/applications/smart-irrigation/region/region.component';
import { SmartIrrigationParksComponent } from './views/applications/smart-irrigation/parks/parks.component';
import { SmartIrrigationSiteComponent } from './views/applications/smart-irrigation/site/site.component';
import { SmartIrrigationSensorTrendComponent } from './views/applications/smart-irrigation/sensor-trend/sensor-trend.component';
import { SmartIrrigationWeatherTrendComponent } from './views/applications/smart-irrigation/weather-trend/weather-trend.component';
import { SmartIrrigationWaterTrendComponent } from './views/applications/smart-irrigation/water-trend/water-trend.component';
import { FireCameraViewer } from './components/fire-camera-viewer/fire-camera-viewer.component';
import { UriSelectComponent } from './components/uri-select/uri-select.component';
import { TelemetryMapWidgetComponent } from './views/dashboard/widgets/telemetry-map-widget/telemetry-map-widget.component';
import { FireForesightIncidentsWatchComponent } from './views/applications/fire-foresight/incidents-watch/fire-foresight-incidents-watch.component';
import { GeoCastingService } from 'src/api/services/geo-casting.service';
import { SmartIrrigationAdministrationComponent } from './views/applications/smart-irrigation/administration/administration.component';
import { SmartIrrigationSystemHealthComponent } from './views/applications/smart-irrigation/system-health/system-health.component';
import { NodeTreeItemComponent } from './views/applications/smart-irrigation/administration/node-tree-item/node-tree-item.component';
import { FireForesightCommandComponent } from './views/applications/fire-foresight/command-centre/fire-foresight-command.component';
import { IncidentFeedComponent } from './components/incident-feed/incident-feed.component';
import { CommandCentreMapComponent } from './components/command-centre-map/command-centre-map.component';
import { SupportTicketEditorModalComponent } from './components/support-ticket-editor-modal/support-ticket-editor-modal.component';

const monacoConfig: NgxMonacoEditorConfig = {
  baseUrl: 'assets',
  defaultOptions: {
    automaticLayout: true,
    scrollBeyondLastLine: false,
    minimap: { enabled: false }
  }
};

export function initializerFactory(env: ApplicationConfigService): () => Promise<any> {
  const promise = env.init().then((value) => {
    console.log("APP_INITIALIZER: ApplicationConfigService has finished loading.");
  });
  return () => promise;
}


export function loggerCallback(logLevel: LogLevel, message: string) {
  //console.log(message);
}

export function msalInstanceFactory(config: ApplicationConfigService): IPublicClientApplication {
  console.log("Attempting to run msalInstanceFactory...");

  const settings = config.getSettings();

  // Defensive Check: This is the most important part.
  // If we've reached here and the clientId is missing, the app cannot continue.
  if (!settings || !settings.azureClientId) {
    throw new Error("FATAL: MSAL Instance factory was called before ApplicationConfigService finished loading settings. The azureClientId is missing.");
  }

  let port = "";
  // Added a check to ensure window.location.port is not an empty string
  if (window.location.port && window.location.port !== "80" && window.location.port !== "443") {
    port = ":" + window.location.port;
  }
  // Use window.location.protocol to correctly handle http and https
  const baseUrl = `${window.location.protocol}//${window.location.hostname}${port}/`;

  const c = new PublicClientApplication({
    auth: {
      clientId: settings.azureClientId, // Now we are sure this has a value
      authority: `https://login.microsoftonline.com/${settings.azureTenantId}`,
      redirectUri: baseUrl,
      postLogoutRedirectUri: `${baseUrl}#/logout`,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Verbose,
        loggerCallback,
        piiLoggingEnabled: false
      }
    }
  });

  console.log("msalInstanceFactory successfully created instance with Client ID:", settings.azureClientId);
  return c;
}

export function msalGuardConfigFactory(config: ApplicationConfigService): MsalGuardConfiguration {
  const c = {
    interactionType: InteractionType.Popup, // Using popup instead of redirect
    authRequest: { scopes: ['user.read'] }
  };
  console.log("msalGuardConfigFactory",c);
  return c as MsalGuardConfiguration;
}

export function msalInterceptorConfigFactory(config: ApplicationConfigService): MsalInterceptorConfiguration {
  const c = {
    interactionType: InteractionType.Popup,
    protectedResourceMap: new Map([
      ['https://graph.microsoft.com/v1.0/me', ['user.read']],
      ['/api', [`api://${config.getSettings().azureClientId}/write`,`api://${config.getSettings().azureClientId}/read`]]
    ])
  };
  console.log("msalInterceptorConfigFactory", c);
  return c as MsalInterceptorConfiguration;
}


//const authProviders = [];
const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

// authProviders.push([
//   { provide: APP_INITIALIZER, useFactory: initializerFactory, deps: [ApplicationConfigService], multi: true },
//   { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true },
//   MsalService,
//   MsalBroadcastService,
//   MsalGuard,AzureAuthGuard,
//   { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory, deps: [ApplicationConfigService] },
//   { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory, deps: [ApplicationConfigService] },
//   { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory, deps: [ApplicationConfigService] }
// ]);

@NgModule({
  declarations: [
    AppComponent,
    DynamicTemplateComponent,
    LoginComponent,UnauthorisedComponent,LogoutComponent,SmartImageComponent,SmartImageListComponent,
    NgForIfEmpty,DateTimeComponent,

    // market place items
    MarketplaceComponent,
    CeresTagHomeComponent,CeresTagOnBoardComponent,CeresTagFarmComponent,
    RoomAirQualityHomeComponent,
    FireForesightOnBoardComponent,
    FireForesightMenuComponent,FireForesightIncidentComponent,FireForesightCameraComponent,FireForesightIncidentEmbeddedComponent,FireForesightCamerasComponent,FireForesightIncidentsComponent,FireForesightStatisticsComponent,FireForesightBurnListComponent,FireForesightBurnListOverviewComponent,FireForesightBurnCoupsComponent,FireForesightBurnListMapComponent,FireForesightWatchComponent,WatchCameraViewComponent,CameraSelectionModalComponent,CameraWatchComponent,GenericCameraSelectionModalComponent,
    FireForesightCommandComponent,
    BlankLayoutComponent,NavigationComponent,BasicLayoutComponent,HeaderComponent,PublicHeaderComponent,MapLayoutComponent,
    PublicLayoutComponent,EmbedLayoutComponent,FireForesightLayoutComponent,CameraWatchLayoutComponent,MapLayersComponent,GeoAssetsComponent,GeoAssetEditorComponent,DashboardTabsComponent,PanoramaComponent,

    HomeComponent,
    OnBoardingComponent,SiteMapComponent,
    ApiKeysComponent,
    DataAnalysisComponent,
    TableComponent,FileListComponent,
    UserListComponent,
    AlertsComponent,AlarmEditorComponent,
    TimePeriodComponent,UriSelectComponent,
    CustomerListComponent,CustomerSpecificComponent,ProductInstanceSpecificComponent,
    ProductsComponent,VideoPlayerComponent,TagEditorComponent,
    SmartImageComponent,SmartImageListComponent,
    VirtualTimeSeriesEditorComponent,WeeklyScheduleEditorComponent,AutomatedScheduleEditorComponent,
    //WidgetSelectComponent,
    SiteTypesComponent,SiteTypeComponent,SitesComponent,SiteComponent,
    OrganisationalSettingsComponent,
    // Fire Foresight
    FireForesightIncidentsWatchComponent,
    //EUCS
    EucsHomeComponent,
    FlowTotalPipe, EndpointStatusPipe, FindCustomerUsagePipe,CustomerFilterPipe,WindroseHomeComponent,VisibleWidgetPipe,
    SmartIrrigationDashboardComponent,SmartIrrigationOnBoardComponent,SmartIrrigationSchedulesComponent,
    // Cloud
    DashboardComponent,KeyValueEditorComponent,
    ParameterComponent,TransformEditorComponent,
    WidgetDirective, WidgetEditorDirective,
    ImageOverlayComponent,
    GaugeComponent,WeatherStationViewer,
    CameraControllerComponent,
    SupportHomeComponent,
    SupportSearchComponent,//SupportAdminComponent,
    WidgetTextTemplatesComponent,
    // - Widgets
    WidgetComponent, TelemetryMapWidgetComponent, ChartWidgetComponent, FreeTextWidgetComponent, MultiTextWidgetComponent, GaugeWidgetComponent,NewLineWidgetComponent,TableWidgetComponent,LatestSimpleWidgetComponent, WindRoseWidgetComponent, MediaPlayerWidgetComponent,IFrameWidgetComponent, OpenStreetMapWidgetComponent,LatestImageWidgetComponent,LatestAdvancedWidgetComponent,VarianceWidgetComponent,ImageOverlayWidgetComponent,GaugeWidgetComponent,SizeComparisonWidgetComponent,TimeLapseWidgetComponent,ControlPanelWidgetComponent,PieChartWidgetComponent,ClockWidgetComponent,DashboardWidgetComponent,OverdueSitesWidgetComponent,
    // - Editors
    ImageOverlayWidgetEditorComponent,GaugeWidgetEditorComponent,WidgetEditorComponent, ChartWidgetEditorComponent, FreeTextWidgetEditorComponent, GaugeWidgetEditorComponent, NewLineWidgetEditorComponent, TableWidgetEditorComponent,LatestSimpleWidgetEditorComponent,WindRoseWidgetEditorComponent, MediaPlayerWidgetEditorComponent, IFrameWidgetEditorComponent, OpenStreetMapWidgetEditorComponent,TelemetryMapWidgetEditorComponent,LatestImageWidgetEditorComponent,LatestAdvancedWidgetEditorComponent,VarianceWidgetEditorComponent,SizeComparisonWidgetEditorComponent,MultiTextWidgetEditorComponent,DashboardWidgetEditorComponent,OverdueSitesWidgetEditorComponent,
    DashboardsComponent,DistributionComponent,DashboardListComponent,MessageTemplateComponent,TimeLapseWidgetEditorComponent,PieChartWidgetEditorComponent,
    UsersComponent,ControlComponent,ControlPanelWidgetEditorComponent,ClockWidgetEditorComponent,
    //ProductsComponent,
    FireCameraViewer,
    CustomersComponent,
    DatabusOverviewComponent,DatabusPurchaseComponent,DatabusApproveComponent,DatabusHealthComponent,SiteTypeFilterPipe,
    DashboardFilterPipe, RelativeDatePipe, FindUsersByIdsPipe, FindPluginsByCategoryPipe, GetWidgetSizePipe, GetWidgetTypePipe, TimeSeriesSortPipe, LightStackFilter, VarDirective, IFrameResizerDirective, SafePipe, IncidentPipe, //, TestImageAnalysisComponent,
    OpenStreetMapComponent, MapComponent,
    CommandCentreMapComponent, IncidentFeedComponent,
    SupportTicketEditorModalComponent,
    ImageClassifierComponent,TestPageComponent,FireImageClassifierComponent,KeyValueEditorComponent,ImageZoneEditorComponent,GradientSelectorComponent,JsonPopupComponent,
    SmartIrrigationRegionsComponent,
    SmartIrrigationRegionComponent,
    SmartIrrigationParksComponent,
    SmartIrrigationSiteComponent,
    SmartIrrigationSensorTrendComponent,SmartIrrigationWeatherTrendComponent,SmartIrrigationWaterTrendComponent,SmartIrrigationAdministrationComponent,SmartIrrigationSystemHealthComponent,
    NodeTreeItemComponent
  ],
  imports: [
    HttpClientJsonpModule,
    ReactiveFormsModule,TagInputModule,
    BrowserAnimationsModule,
    FormsModule,
    BrowserModule,CommonModule,
    RouterModule,
    AppRoutingModule,
    HttpClientModule,
    TooltipModule,PopoverModule.forRoot(),
    BsDatepickerModule,SortableModule.forRoot(),
    FontAwesomeModule,
    HighchartsChartModule,
    TrendModule,
    NgbDropdownModule, NgbModalModule,
    TabsModule.forRoot(),ToastrModule.forRoot(),CollapseModule,
    ModalModule.forRoot(),BsDatepickerModule.forRoot(),BsDropdownModule.forRoot(),PaginationModule.forRoot(), TooltipModule.forRoot(),
    SortablejsModule.forRoot({ animation: 150 }),
    ColorPickerModule,
    MonacoEditorModule.forRoot(monacoConfig),
    NgxFileDropModule,
    NgbTooltipModule,
    ClipboardModule,
    AngularEditorModule,
    //MsalModule
    // MsalModule.forRoot(
    // msalInstanceFactory(inject(ApplicationConfigService)),
    // msalGuardConfigFactory(inject(ApplicationConfigService)),
    // msalInterceptorConfigFactory(inject(ApplicationConfigService))
    // )
    //MsalModule.forRoot( msalInstanceFactory, msalGuardConfigFactory, msalInterceptorConfigFactory)
  ],
  providers: [
    // eucs
    EucsAlarmDashboardService,
    // other
    CssLoaderService,DashboardEventService,
    CookieService,
    DecimalPipe,
    AzureAuthService, ApplicationConfigService,
    { provide: LocationStrategy, useClass: HashLocationStrategy },
    BsDropdownConfig,
    FireService,GeoCastingService,
    DataBusTableService,UserTableService,CustomerTableService,ProductTableService,DashboardTableService,
    DashboardListTableService,DistributionTableService,SiteTypeTableService,MessageTemplateTableService, MapLayerTableService,
    IconService,ElapsedTimeService,StorageTablesService,DownloadCsvService,ScriptService,
    DashboardService,DataService,CustomerService,
    WidgetService,
    AbstractDataService,
    //...authProviders
// Step A: The APP_INITIALIZER runs first and pauses the app until your config is loaded.
    {
      provide: APP_INITIALIZER,
      useFactory: initializerFactory,
      deps: [ApplicationConfigService],
      multi: true
    },

    // Step B: The MSAL providers now depend on ApplicationConfigService,
    // which is guaranteed to be fully initialized.
    //
    // PublicBypassInterceptor MUST be listed before MsalInterceptor so it runs
    // first in the chain and can set BYPASS_AUTH for anonymous/public routes.
    {
      provide: HTTP_INTERCEPTORS,
      useClass: PublicBypassInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    {
        provide: MSAL_INSTANCE,
        useFactory: msalInstanceFactory, // Your corrected factory
        deps: [ApplicationConfigService]
    },
    {
        provide: MSAL_GUARD_CONFIG,
        useFactory: msalGuardConfigFactory, // This should also use the initialized config
        deps: [ApplicationConfigService]
    },
    {
        provide: MSAL_INTERCEPTOR_CONFIG,
        useFactory: msalInterceptorConfigFactory, // This too
        deps: [ApplicationConfigService]
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    AzureAuthGuard,
  ],

  bootstrap: [AppComponent]
})
export class AppModule { }

